import { RISK_STATUS_LABELS } from './RiskBadges'
import { TaskStatusSchema, type RiskAcceptanceDTO, type RiskAcceptanceRequest } from '@deepguide-ai/types'
/**
 * New sections on the risk detail page: linked tests/assets/vendors,
 * acceptance card, activity feed. Tests (CES) are the RFC-214 risk-UI link
 * surface (controls are hidden from product navigation). The acceptance flow
 * is structured (RFC-178 §4.3) — `acceptRisk` is the only inbound path
 * to treatment=ACCEPT.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { invalidateTaskBoardQueries } from '@/pages/tasks/taskQueryInvalidation'
import { Button } from '@deepguide-ai/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@deepguide-ai/ui/command'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import {
  AlertCircle,
  AlertTriangle,
  Box,
  Building2,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Target,
  TrendingUp,
  Unlink,
  UserRound,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import { getStatusColor } from '@/lib/compliance-status'
import { relativeTime, formatDate, humanizeKey } from '@/lib/format'
import { statusBadgeClasses } from '@/lib/status-colors'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { getFirstName } from '@/utils/user-name'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { DetailSection } from '@/components/compliance/detail-primitives'

const EMPTY_TEXT_CLASS = 'font-body text-[12px] text-text-placeholder'
const SECTION_ACTION_BUTTON_CLASS =
  'h-7 gap-1 px-2 text-[11px] text-text-body hover:bg-accent hover:text-accent-foreground'

// Exported for reuse by the vendor detail's Risks tab (link a risk → vendor):
// the same searchable picker, inverse direction.
export function PickerDialog<T extends { id: string }>({
  open,
  onOpenChange,
  title,
  description,
  items,
  excludedIds,
  searchPlaceholder,
  emptyMessage,
  renderRow,
  itemSearchText,
  footer,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  items: T[]
  excludedIds: Set<string>
  searchPlaceholder: string
  emptyMessage: string
  renderRow: (item: T) => React.ReactNode
  itemSearchText: (item: T) => string
  footer?: React.ReactNode
  onSelect: (item: T) => void
}) {
  const candidates = useMemo(
    () => items.filter((i) => !excludedIds.has(i.id)),
    [items, excludedIds]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg min-w-0 p-0'>
        <DialogHeader className='px-6 pt-5 pb-3'>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className='min-w-0 pb-3'>
          <Command className='max-w-full min-w-0'>
            <CommandInput
              icon={<Search className='size-4 shrink-0 opacity-50' />}
              placeholder={searchPlaceholder}
              wrapperClassName='border-input mx-3 mb-2 rounded-sm border bg-card px-3'
            />
            <ScrollArea
              className='w-full max-w-full min-w-0 overflow-hidden'
              viewClassName='max-h-[300px] px-3 [&>div]:!block'
            >
              <CommandList className='max-h-none w-full min-w-0 overflow-x-hidden'>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {candidates.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={itemSearchText(item)}
                      onSelect={() => onSelect(item)}
                      className='min-w-0 break-words [&>*]:min-w-0'
                    >
                      {renderRow(item)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </ScrollArea>
          </Command>
        </div>
        {footer && (
          <div className='border-border border-t px-6 py-4'>{footer}</div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Linked Tests (RFC-214) ─────────────────────────────────────────────

type LinkedTest = {
  cesId: string
  displayId: string | null
  title: string
  status: string | null
  lastTestedAt: Date | string | null
  updatedAt: Date | string | null
  dueDate: Date | string | null
  linkSource: string
  rationale: string | null
}

export function LinkedTestsSection({
  riskId,
  tests,
  onChanged,
  readOnly = false,
  allowReadOnlyLinks = false,
}: {
  riskId: string
  tests: LinkedTest[]
  onChanged: () => void
  readOnly?: boolean
  allowReadOnlyLinks?: boolean
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const queryClient = useQueryClient()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }

  const allTests = useQuery(
    trpc.riskRegister.listTestsForPicker.queryOptions(undefined, {
      enabled: pickerOpen,
    })
  )
  const linkMutation = useMutation(trpc.riskRegister.linkTest.mutationOptions())
  const unlinkMutation = useMutation(
    trpc.riskRegister.unlinkTest.mutationOptions()
  )

  // Residual scoring reads linked-test status, so refresh it alongside the row.
  const refresh = () => {
    onChanged()
    queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.suggestResidual.queryKey({ riskId }),
    })
  }

  const onLink = async (cesId: string) => {
    await linkMutation.mutateAsync({ riskId, cesId })
    setPickerOpen(false)
    refresh()
  }
  const onUnlink = async (cesId: string) => {
    await unlinkMutation.mutateAsync({ riskId, cesId })
    refresh()
  }

  const existingIds = useMemo(() => new Set(tests.map((t) => t.cesId)), [tests])

  return (
    <DetailSection
      title='Linked Tests'
      actionPosition='right'
      headerActions={
        readOnly ? null : (
          <Button
            variant='ghost'
            size='sm'
            className={SECTION_ACTION_BUTTON_CLASS}
            onClick={() => setPickerOpen(true)}
          >
            <Plus className='size-3' /> Link Test
          </Button>
        )
      }
    >
      {tests.length === 0 ? (
        <TableEmptyState className='min-h-0 px-4 py-4'>
          <p className={EMPTY_TEXT_CLASS}>
            No tests linked. Add the tests that verify this risk is controlled.
          </p>
        </TableEmptyState>
      ) : (
        <div className='border-border bg-card rounded-sm border'>
          <ul className='divide-border divide-y'>
            {tests.map((t) => (
              <li
                key={t.cesId}
                className='flex items-center justify-between gap-3 px-4 py-1.5'
              >
                <div className='flex min-w-0 flex-1 flex-col'>
                  {readOnly && !allowReadOnlyLinks ? (
                    <span className='font-body text-text-secondary min-w-0 truncate text-[12px]'>
                      {t.displayId && (
                        <span className='text-text-subtle mr-1.5 font-mono text-[11px]'>
                          {t.displayId}
                        </span>
                      )}
                      {t.title || t.cesId}
                    </span>
                  ) : (
                    <Link
                      to='/workspaces/$workspaceId/tests/$testId'
                      params={{ workspaceId, testId: t.cesId }}
                      className='font-body text-cobalt-strong min-w-0 truncate text-[12px] hover:underline'
                    >
                      {t.displayId && (
                        <span className='text-text-subtle mr-1.5 font-mono text-[11px]'>
                          {t.displayId}
                        </span>
                      )}
                      {t.title || t.cesId}
                    </Link>
                  )}
                </div>
                <span className='font-body text-text-placeholder shrink-0 text-[10px]'>
                  {formatTimestamp(t.lastTestedAt ?? t.updatedAt)}
                </span>
                {t.status && <TestStatusBadge status={t.status} />}
                {!readOnly && t.linkSource === 'manual' && (
                  <button
                    type='button'
                    onClick={() => onUnlink(t.cesId)}
                    disabled={unlinkMutation.isPending}
                    className='font-body text-text-subtle hover:bg-muted hover:text-status-red inline-flex size-6 items-center justify-center rounded-sm disabled:opacity-40'
                    aria-label='Unlink test'
                  >
                    <X className='size-3.5' />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title='Link a test'
        description='Pick a test that verifies this risk is controlled.'
        items={allTests.data ?? []}
        excludedIds={existingIds}
        searchPlaceholder='Search tests…'
        emptyMessage={allTests.isLoading ? 'Loading…' : 'No tests found.'}
        itemSearchText={(t) => `${t.displayId ?? ''} ${t.title}`}
        renderRow={(t) => (
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span className='text-text-secondary text-[12px] font-medium'>
              {t.displayId ?? t.title}
            </span>
            {t.displayId && (
              <span className='text-text-subtle truncate text-[11px]'>
                {t.title}
              </span>
            )}
          </div>
        )}
        onSelect={(t) => onLink(t.id)}
      />
    </DetailSection>
  )
}

// ── Affected Assets ───────────────────────────────────────────────────

type LinkedAsset = {
  assetId: string
  name: string
  assetType: string | null
  criticality: string | null
  sequenceNumber: number | null
}

export function AffectedAssetsSection({
  riskId,
  assets,
  onChanged,
  readOnly = false,
  allowReadOnlyLinks = false,
}: {
  riskId: string
  assets: LinkedAsset[]
  onChanged: () => void
  readOnly?: boolean
  allowReadOnlyLinks?: boolean
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }
  const allAssets = useQuery(
    trpc.riskRegister.listAssetsForPicker.queryOptions(undefined, {
      enabled: pickerOpen,
    })
  )
  const linkMutation = useMutation(
    trpc.riskRegister.linkAsset.mutationOptions()
  )
  const unlinkMutation = useMutation(
    trpc.riskRegister.unlinkAsset.mutationOptions()
  )

  const onLink = async (assetId: string) => {
    await linkMutation.mutateAsync({ riskId, assetId })
    setPickerOpen(false)
    onChanged()
  }
  const onUnlink = async (assetId: string) => {
    await unlinkMutation.mutateAsync({ riskId, assetId })
    onChanged()
  }

  const existingIds = useMemo(
    () => new Set(assets.map((a) => a.assetId)),
    [assets]
  )

  return (
    <DetailSection
      title='Affected Assets'
      className='[&>div:first-child]:min-h-7'
      actionPosition='right'
      headerActions={
        readOnly ? null : (
          <Button
            variant='ghost'
            size='sm'
            className={SECTION_ACTION_BUTTON_CLASS}
            onClick={() => setPickerOpen(true)}
          >
            <Plus className='size-3' /> Link Asset
          </Button>
        )
      }
    >
      {assets.length === 0 ? (
        <TableEmptyState className='min-h-0 px-4 py-4'>
          <p className={EMPTY_TEXT_CLASS}>No assets linked to this risk.</p>
        </TableEmptyState>
      ) : (
        <div className='border-border bg-card rounded-sm border'>
          <ul className='divide-border divide-y'>
            {assets.map((a) => (
              <li
                key={a.assetId}
                className='flex items-center justify-between gap-3 px-4 py-1.5'
              >
                <div className='flex min-w-0 flex-col gap-1'>
                  {readOnly && !allowReadOnlyLinks ? (
                    <span className='font-body text-text-secondary truncate text-[12px] font-medium'>
                      {a.sequenceNumber
                        ? `AST-${String(a.sequenceNumber).padStart(3, '0')} `
                        : ''}
                      {a.name}
                    </span>
                  ) : (
                    <Link
                      to='/workspaces/$workspaceId/assets/$assetId'
                      params={{ workspaceId, assetId: a.assetId }}
                      className='font-body text-cobalt-strong truncate text-[12px] font-medium hover:underline'
                    >
                      {a.sequenceNumber
                        ? `AST-${String(a.sequenceNumber).padStart(3, '0')} `
                        : ''}
                      {a.name}
                    </Link>
                  )}
                  <div className='font-body text-text-subtle flex items-center gap-2 text-[11px]'>
                    {a.assetType && <span>{a.assetType}</span>}
                    {a.criticality && (
                      <>
                        <span>·</span>
                        <span>{a.criticality}</span>
                      </>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <button
                    type='button'
                    onClick={() => onUnlink(a.assetId)}
                    disabled={unlinkMutation.isPending}
                    className='font-body text-text-subtle hover:bg-muted hover:text-status-red inline-flex size-6 items-center justify-center rounded-sm disabled:opacity-40'
                    aria-label='Unlink asset'
                  >
                    <X className='size-3.5' />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title='Link an asset'
        description='Pick an asset affected by this risk.'
        items={allAssets.data ?? []}
        excludedIds={existingIds}
        searchPlaceholder='Search assets…'
        emptyMessage={allAssets.isLoading ? 'Loading…' : 'No assets found.'}
        itemSearchText={(a) => `${a.name} ${a.assetType ?? ''}`}
        renderRow={(a) => (
          <div className='flex flex-col gap-0.5'>
            <span className='text-text-secondary text-[12px] font-medium'>
              {a.name}
            </span>
            <span className='text-text-subtle text-[11px]'>
              {a.assetType ?? ''}
              {a.criticality ? ` · ${a.criticality}` : ''}
            </span>
          </div>
        )}
        onSelect={(a) => onLink(a.id)}
      />
    </DetailSection>
  )
}

// ── Linked Tasks ──────────────────────────────────────────────────────
// Tasks carrying `riskItemId` (created from the Assess dialog, or linked
// here). Link/unlink is `task.update({ riskItemId })` — no join table.

export function LinkedTasksSection({
  riskId,
  riskAssessmentId,
  readOnly = false,
  outstandingOnly = false,
}: {
  riskId: string
  riskAssessmentId?: string
  readOnly?: boolean
  outstandingOnly?: boolean
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const queryClient = useQueryClient()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }

  const linked = useQuery(
    trpc.task.list.queryOptions({
      riskItemId: riskId,
      riskAssessmentId,
      ...(outstandingOnly ? { status: TaskStatusSchema.options.filter(status => status !== 'RESOLVED') } : { includeResolved: true }),
      limit: 200,
    })
  )
  const candidates = useQuery(
    trpc.task.list.queryOptions({ limit: 200 }, { enabled: pickerOpen })
  )
  const updateMutation = useMutation(trpc.task.update.mutationOptions())

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: trpc.task.list.queryKey() })
    await invalidateTaskBoardQueries(queryClient)
  }
  const setLink = async (taskId: string, riskItemId: string | null) => {
    await updateMutation.mutateAsync({ taskId, riskItemId })
    setPickerOpen(false)
    await refresh()
  }

  const tasks = (linked.data?.items ?? []).filter(task => !outstandingOnly || task.status !== 'RESOLVED')
  const existingIds = new Set(tasks.map((t) => t.id))

  return (
    <DetailSection
      title='Linked Tasks'
      actionPosition='right'
      headerActions={
        readOnly ? null : (
          <Button
            variant='ghost'
            size='sm'
            className={SECTION_ACTION_BUTTON_CLASS}
            onClick={() => setPickerOpen(true)}
          >
            <Plus className='size-3' /> Link Task
          </Button>
        )
      }
    >
      {tasks.length === 0 ? (
        <TableEmptyState className='min-h-0 px-4 py-4'>
          <p className={EMPTY_TEXT_CLASS}>
            {linked.isLoading
              ? 'Loading…'
              : 'No tasks linked. Link the work that remediates this risk.'}
          </p>
        </TableEmptyState>
      ) : (
        <div className='border-border bg-card rounded-sm border'>
          <ul className='divide-border divide-y'>
            {tasks.map((t) => (
              <li
                key={t.id}
                className='flex items-center justify-between gap-3 px-4 py-1.5'
              >
                <Link
                  to='/workspaces/$workspaceId/tasks/$taskId'
                  params={{ workspaceId, taskId: t.id }}
                  className='font-body text-cobalt-strong min-w-0 flex-1 truncate text-[12px] hover:underline'
                >
                  <span className='text-text-subtle mr-1.5 font-mono text-[11px]'>
                    {t.displayCode}
                  </span>
                  {t.title}
                </Link>
                {t.assignedTo && (
                  <MemberAvatar
                    name={t.assignedTo.name}
                    email={t.assignedTo.email}
                    image={t.assignedTo.image}
                    size='xs'
                  />
                )}
                <TestStatusBadge status={t.status} />
                {!readOnly && (
                  <button
                    type='button'
                    onClick={() => setLink(t.id, null)}
                    disabled={updateMutation.isPending}
                    className='font-body text-text-subtle hover:bg-muted hover:text-status-red inline-flex size-6 items-center justify-center rounded-sm disabled:opacity-40'
                    aria-label='Unlink task'
                  >
                    <X className='size-3.5' />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title='Link a task'
        description='Pick an open task that remediates this risk.'
        items={candidates.data?.items ?? []}
        excludedIds={existingIds}
        searchPlaceholder='Search tasks…'
        emptyMessage={
          candidates.isLoading ? 'Loading…' : 'No open tasks found.'
        }
        itemSearchText={(t) => `${t.displayCode} ${t.title}`}
        renderRow={(t) => (
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span className='text-text-secondary text-[12px] font-medium'>
              {t.displayCode}
            </span>
            <span className='text-text-subtle truncate text-[11px]'>
              {t.title}
            </span>
          </div>
        )}
        onSelect={(t) => setLink(t.id, riskId)}
      />
    </DetailSection>
  )
}

// ── Related Vendors ───────────────────────────────────────────────────

type LinkedVendor = {
  vendorId: string
  name: string
  category: string | null
  riskTier: string | null
  baaStatus: string | null
  sequenceNumber: number | null
}

export function RelatedVendorsSection({
  riskId,
  vendors,
  onChanged,
  readOnly = false,
  allowReadOnlyLinks = false,
}: {
  riskId: string
  vendors: LinkedVendor[]
  onChanged: () => void
  readOnly?: boolean
  allowReadOnlyLinks?: boolean
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }
  const allVendors = useQuery(
    trpc.riskRegister.listVendorsForPicker.queryOptions(undefined, {
      enabled: pickerOpen,
    })
  )
  const linkMutation = useMutation(
    trpc.riskRegister.linkVendor.mutationOptions()
  )
  const unlinkMutation = useMutation(
    trpc.riskRegister.unlinkVendor.mutationOptions()
  )

  const onLink = async (vendorId: string) => {
    await linkMutation.mutateAsync({ riskId, vendorId })
    setPickerOpen(false)
    onChanged()
  }
  const onUnlink = async (vendorId: string) => {
    await unlinkMutation.mutateAsync({ riskId, vendorId })
    onChanged()
  }

  const existingIds = useMemo(
    () => new Set(vendors.map((v) => v.vendorId)),
    [vendors]
  )

  return (
    <DetailSection
      title='Related Vendors'
      className='[&>div:first-child]:min-h-7'
      actionPosition='right'
      headerActions={
        readOnly ? null : (
          <Button
            variant='ghost'
            size='sm'
            className={SECTION_ACTION_BUTTON_CLASS}
            onClick={() => setPickerOpen(true)}
          >
            <Plus className='size-3' /> Link Vendor
          </Button>
        )
      }
    >
      {vendors.length === 0 ? (
        <TableEmptyState className='min-h-0 px-4 py-4'>
          <p className={EMPTY_TEXT_CLASS}>No vendors linked to this risk.</p>
        </TableEmptyState>
      ) : (
        <div className='border-border bg-card rounded-sm border'>
          <ul className='divide-border divide-y'>
            {vendors.map((v) => (
              <li
                key={v.vendorId}
                className='flex items-center justify-between gap-3 px-4 py-1.5'
              >
                <div className='flex min-w-0 flex-col gap-1'>
                  {readOnly && !allowReadOnlyLinks ? (
                    <span className='font-body text-text-secondary truncate text-[12px] font-medium'>
                      {v.sequenceNumber
                        ? `VND-${String(v.sequenceNumber).padStart(3, '0')} `
                        : ''}
                      {v.name}
                    </span>
                  ) : (
                    <Link
                      to='/workspaces/$workspaceId/vendors/$vendorId'
                      params={{ workspaceId, vendorId: v.vendorId }}
                      className='font-body text-cobalt-strong truncate text-[12px] font-medium hover:underline'
                    >
                      {v.sequenceNumber
                        ? `VND-${String(v.sequenceNumber).padStart(3, '0')} `
                        : ''}
                      {v.name}
                    </Link>
                  )}
                  <div className='font-body text-text-subtle flex items-center gap-2 text-[11px]'>
                    {v.category && <span>{v.category}</span>}
                    {v.riskTier && (
                      <>
                        <span>·</span>
                        <span>{v.riskTier}</span>
                      </>
                    )}
                    {v.baaStatus && v.baaStatus !== 'NOT_REQUIRED' && (
                      <>
                        <span>·</span>
                        <span>BAA: {v.baaStatus}</span>
                      </>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <button
                    type='button'
                    onClick={() => onUnlink(v.vendorId)}
                    disabled={unlinkMutation.isPending}
                    className='font-body text-text-subtle hover:bg-muted hover:text-status-red inline-flex size-6 items-center justify-center rounded-sm disabled:opacity-40'
                    aria-label='Unlink vendor'
                  >
                    <X className='size-3.5' />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title='Link a vendor'
        description='Pick a vendor affected by or contributing to this risk.'
        items={allVendors.data ?? []}
        excludedIds={existingIds}
        searchPlaceholder='Search vendors…'
        emptyMessage={allVendors.isLoading ? 'Loading…' : 'No vendors found.'}
        itemSearchText={(v) => `${v.name} ${v.category ?? ''}`}
        renderRow={(v) => (
          <div className='flex flex-col gap-0.5'>
            <span className='text-text-secondary text-[12px] font-medium'>
              {v.name}
            </span>
            <span className='text-text-subtle text-[11px]'>
              {v.category ?? ''}
              {v.riskTier ? ` · ${v.riskTier}` : ''}
            </span>
          </div>
        )}
        onSelect={(v) => onLink(v.id)}
      />
    </DetailSection>
  )
}

// ── Verifying Tests ───────────────────────────────────────────────────

function TestStatusBadge({ status }: { status: string }) {
  const color = getStatusColor(status)
  return (
    <span
      className={cn(
        'font-body inline-flex h-5 items-center rounded-sm px-2 text-[10px] font-medium tracking-[0.04em] whitespace-nowrap uppercase',
        color === 'muted'
          ? 'bg-muted text-muted-foreground'
          : statusBadgeClasses[color]
      )}
    >
      {status.replaceAll('_', ' ')}
    </span>
  )
}

function formatTimestamp(date: Date | string | null | undefined): string {
  if (!date) return '—'
  return relativeTime(date, { suffix: true })
}

// ── Acceptance card + Accept dialog ──────────────────────────────────

export type AcceptanceData = RiskAcceptanceDTO

export function AcceptanceCard({
  canDecide, canRequestApproval, readOnly = false, onRequestApproval,
  riskId,
  acceptance,
  onChanged,
}: {
  riskId: string
  acceptance: AcceptanceData
  canDecide: boolean
  canRequestApproval: boolean
  readOnly?: boolean
  onRequestApproval: () => void
  onChanged: () => void
}) {
  const [extendOpen, setExtendOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const decisionMutation = useMutation(trpc.riskRegister.decideAcceptance.mutationOptions())
  const [decisionError, setDecisionError] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const decide = async (decision: 'approve' | 'reject') => {
    setDecisionError(null)
    try {
      await decisionMutation.mutateAsync({ riskId, requestedAt: acceptance.requestedAt, decision, reason: rejectionReason || undefined })
      onChanged()
    } catch (err) { setDecisionError(err instanceof Error ? err.message : 'Unable to record decision') }
  }
  const pending = acceptance.status === 'PENDING'
  const rejected = acceptance.status === 'REVOKED'

  const expiresAt = new Date(acceptance.expiresAt)
  const isExpired =
    acceptance.status === 'EXPIRED' || expiresAt.getTime() < Date.now()
  const daysLeft = Math.ceil(
    (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  )

  const approver =
    acceptance.approverName || acceptance.approverEmail || 'an approver'

  return (
    <>
      <div
        className={cn(
          'rounded-sm border p-3',
          isExpired
            ? 'border-status-red/30 bg-status-red-bg'
            : 'border-status-blue-border bg-status-blue-subtle'
        )}
      >
        <div className='mb-2 flex items-center gap-2'>
          {isExpired ? (
            <AlertTriangle className='text-status-red size-3.5' />
          ) : (
            <ShieldCheck className='text-cobalt-strong size-3.5' />
          )}
          <span
            className={cn(
              'font-body text-[11px] font-semibold tracking-wide uppercase',
              isExpired ? 'text-status-red' : 'text-status-blue-text'
            )}
          >
            {pending ? 'Exception requested' : rejected ? 'Acceptance revoked or request rejected' : isExpired ? 'Acceptance expired' : 'Risk accepted'}
          </span>
        </div>
        <p className='font-body text-text-secondary text-[12px]'>
          {pending ? acceptance.approverId ? `Awaiting a decision from ${approver}. Selecting an approver does not approve this request.` : 'Choose an authorized approver to review this request.'
            : rejected ? acceptance.revokedReason
              : <>Accepted by <strong>{approver}</strong> on {acceptance.approvedAt ? new Date(acceptance.approvedAt).toLocaleDateString() : '—'}.{' '}
                {isExpired ? `Expired ${expiresAt.toLocaleDateString()}.` : `Expires ${expiresAt.toLocaleDateString()} (${daysLeft}d).`}</>}
          {pending && ` The policy requirement remains in place until approval. Proposed expiry: ${expiresAt.toLocaleDateString()}.`}
        </p>
        {acceptance.justification && (
          <div className='mt-2'>
            <button
              type='button'
              onClick={() => setExpanded((v) => !v)}
              className='font-body text-cobalt-strong text-[11px] hover:underline'
            >
              {expanded ? 'Hide' : 'Show'} justification
            </button>
            {expanded && (
              <p className='font-body text-text-body mt-1.5 text-[12px] whitespace-pre-wrap'>
                {acceptance.justification}
              </p>
            )}
          </div>
        )}
        {!readOnly && pending && <div className='mt-3 space-y-2'>
          {canDecide ? <>
            <textarea aria-label='Rejection reason' value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} rows={2}
              className='border-border bg-card w-full rounded-sm border p-2 text-xs' placeholder='Reason (required to reject)' />
            <div className='flex gap-2'>
              <Button size='sm' disabled={decisionMutation.isPending || expiresAt.getTime() <= Date.now()} onClick={() => void decide('approve')}>Approve acceptance</Button>
              <Button variant='outline' size='sm' disabled={decisionMutation.isPending || !rejectionReason.trim()} onClick={() => void decide('reject')}>Reject request</Button>
              <Button variant='ghost' size='sm' disabled={decisionMutation.isPending} onClick={onRequestApproval}>Change approver or expiry</Button>
            </div>
          </> : canRequestApproval ? <Button variant='outline' size='sm' onClick={onRequestApproval}>{acceptance.approverId ? 'Change approver or expiry' : 'Choose approver and expiry'}</Button>
            : <p className='text-text-subtle text-xs'>An administrator must assign an approver and expiry.</p>}
          {decisionError && <p role='alert' className='text-status-red text-xs'>{decisionError}</p>}
        </div>}
        {!readOnly && rejected && canRequestApproval && <Button variant='outline' size='sm' className='mt-3' onClick={onRequestApproval}>Request new approval</Button>}
        {!readOnly && !pending && !rejected && <div className='mt-3 flex gap-2'>
          {canDecide && !isExpired && <Button variant='outline' size='sm' className='h-7 rounded-sm px-2 text-[11px]' onClick={() => setExtendOpen(true)}>Extend</Button>}
          <Button variant='outline' size='sm' className='border-status-red/30 text-status-red h-7 rounded-sm px-2 text-[11px]' onClick={() => setRevokeOpen(true)}>Revoke</Button>
        </div>}
      </div>

      <ExtendAcceptanceDialog
        open={extendOpen}
        onOpenChange={setExtendOpen}
        riskId={riskId}
        currentExpiresAt={expiresAt}
        onDone={onChanged}
      />
      <RevokeAcceptanceDialog
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
        riskId={riskId}
        onDone={onChanged}
      />
    </>
  )
}

export function AcceptRiskDialog({
  initialAcceptance,
  initialRequest,
  onPrepare,
  open,
  onOpenChange,
  riskId,
  onAccepted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  riskId: string
  onAccepted: () => void
  initialAcceptance?: RiskAcceptanceDTO | null
  initialRequest?: RiskAcceptanceRequest | null
  onPrepare?: (request: RiskAcceptanceRequest) => void
}) {
  const approversQuery = useQuery(
    trpc.riskRegister.listEligibleApprovers.queryOptions({ riskId })
  )
  const acceptMutation = useMutation(
    trpc.riskRegister.acceptRisk.mutationOptions()
  )
  const [justification, setJustification] = useState(initialRequest?.justification ?? initialAcceptance?.justification ?? '')
  const [approverId, setApproverId] = useState<string | undefined>(initialRequest?.approverId ?? initialAcceptance?.approverId ?? undefined)
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(() => {
    if (initialRequest) return new Date(initialRequest.expiresAt)
    if (initialAcceptance && new Date(initialAcceptance.expiresAt).getTime() > Date.now()) return new Date(initialAcceptance.expiresAt)
    const d = new Date()
    d.setFullYear(d.getFullYear() + 1)
    return d
  })
  const [error, setError] = useState<string | null>(null)

  const approvers = approversQuery.data?.approvers ?? []

  const canSubmit =
    justification.trim().length >= 20 &&
    !!approverId &&
    !!expiresAt &&
    expiresAt.getTime() > Date.now() &&
    !acceptMutation.isPending

  const onSubmit = async () => {
    if (!canSubmit || !approverId || !expiresAt) return
    setError(null)
    if (onPrepare) {
      onPrepare({ justification: justification.trim(), approverId, expiresAt })
      return
    }
    try {
      await acceptMutation.mutateAsync({
        riskId,
        justification: justification.trim(),
        approverId,
        expiresAt,
      })
      onAccepted()
    } catch (e: any) {
      setError(e?.message ?? 'Failed to accept risk')
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!acceptMutation.isPending) onOpenChange(nextOpen)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request risk acceptance</DialogTitle>
          <DialogDescription>
            {initialAcceptance?.status === 'PENDING'
              ? 'Update the approver or expiry. The request stays pending, even if you choose yourself. The assigned approver must explicitly approve it.'
              : "Document the rationale, choose an authorized approver, and set an expiry date. Another person's approval stays pending until they make a decision. Choosing yourself records your explicit approval when permitted."}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 px-6'>
          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              Justification
            </label>
            <textarea
              readOnly={initialAcceptance?.questionnaireException}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={3}
              minLength={20}
              maxLength={4000}
              placeholder='Why is this risk being accepted? (min 20 chars)'
              className='font-body border-border-divider text-text-secondary focus-visible:border-ring focus-visible:ring-ring/50 w-full rounded-sm border px-3 py-2 text-[12px] focus-visible:ring-[3px] focus-visible:outline-none'
            />
            <p className='font-body text-text-placeholder text-[10px]'>
              {justification.length}/20 min
              {initialAcceptance?.questionnaireException && ' · Edit the rationale in the originating questionnaire to request a changed exception.'}
            </p>
          </div>

          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              Approver
            </label>
            <Select value={approverId} onValueChange={setApproverId}>
              <SelectTrigger fullWidth className='h-9 text-[12px]'>
                <SelectValue placeholder='Select approver' />
              </SelectTrigger>
              <SelectContent>
                {approvers.map((a: any) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name ?? a.email ?? a.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {approversQuery.data?.enforceCreatorApproverSeparation && (
              <p className='font-body text-text-placeholder text-[10px]'>
                Separation of duties is enforced: you cannot approve your own
                acceptance.
              </p>
            )}
          </div>

          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              Expires
            </label>
            <DatePicker
              value={expiresAt}
              onChange={(d) => setExpiresAt(d ?? undefined)}
              placeholder='Set expiry date'
              className='h-9 w-full rounded-sm'
            />
          </div>

          {error && (
            <p className='font-body text-status-red text-[11px]'>{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='ghost'
            onClick={() => onOpenChange(false)}
            disabled={acceptMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {onPrepare ? 'Use in review' : acceptMutation.isPending ? 'Saving…' : initialAcceptance?.status === 'PENDING' ? 'Save changes' : approverId === approversQuery.data?.currentUserId ? 'Approve acceptance' : 'Request approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ExtendAcceptanceDialog({
  open,
  onOpenChange,
  riskId,
  currentExpiresAt,
  onDone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  riskId: string
  currentExpiresAt: Date
  onDone: () => void
}) {
  const mutation = useMutation(
    trpc.riskRegister.extendAcceptance.mutationOptions()
  )
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(() => {
    const d = new Date(currentExpiresAt)
    d.setFullYear(d.getFullYear() + 1)
    return d
  })
  const [justification, setJustification] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async () => {
    if (!expiresAt || expiresAt.getTime() <= Date.now()) {
      setError('Expiry date must be in the future')
      return
    }
    setError(null)
    try {
      await mutation.mutateAsync({
        riskId,
        expiresAt,
        justification: justification.trim() || undefined,
      })
      onDone()
      onOpenChange(false)
    } catch (e: any) {
      setError(e?.message ?? 'Failed to extend')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend acceptance</DialogTitle>
          <DialogDescription>
            Push the expiry date forward. The auditor-visible activity log will
            record the change.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3 px-6'>
          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              New expiry
            </label>
            <DatePicker
              value={expiresAt}
              onChange={(d) => setExpiresAt(d ?? undefined)}
              placeholder='New expiry date'
              className='h-9 w-full rounded-md'
            />
          </div>
          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              Justification (optional)
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={3}
              placeholder='Why is the extension warranted?'
              className='font-body border-border-divider w-full rounded-md border px-2 py-1.5 text-[12px]'
            />
          </div>
          {error && (
            <p className='font-body text-status-red text-[11px]'>{error}</p>
          )}
        </div>
        <DialogFooter className='px-6 pb-5'>
          <Button
            variant='ghost'
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Extend'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RevokeAcceptanceDialog({
  open,
  onOpenChange,
  riskId,
  onDone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  riskId: string
  onDone: () => void
}) {
  const mutation = useMutation(
    trpc.riskRegister.revokeAcceptance.mutationOptions()
  )
  const [reason, setReason] = useState('')
  const [newTreatment, setNewTreatment] = useState<'MITIGATE' | 'AVOID'>(
    'MITIGATE'
  )
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async () => {
    if (!reason.trim()) {
      setError('Reason is required')
      return
    }
    setError(null)
    try {
      await mutation.mutateAsync({
        riskId,
        reason: reason.trim(),
        newTreatment,
      })
      onDone()
      onOpenChange(false)
    } catch (e: any) {
      setError(e?.message ?? 'Failed to revoke')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke acceptance</DialogTitle>
          <DialogDescription>
            The acceptance record will be marked revoked. The risk will move to
            the chosen treatment.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3 px-6'>
          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              Reason
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder='Why is this acceptance being revoked?'
              className='font-body border-border-divider w-full rounded-md border px-2 py-1.5 text-[12px]'
            />
          </div>
          <div className='space-y-1.5'>
            <label className='font-body text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
              New treatment
            </label>
            <Select
              value={newTreatment}
              onValueChange={(v) => setNewTreatment(v as any)}
            >
              <SelectTrigger className='h-9 text-[12px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='MITIGATE'>Mitigate</SelectItem>
                <SelectItem value='AVOID'>Avoid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && (
            <p className='font-body text-status-red text-[11px]'>{error}</p>
          )}
        </div>
        <DialogFooter className='px-6 pb-5'>
          <Button
            variant='ghost'
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={mutation.isPending}
            className='bg-status-red hover:bg-[#A52E22]'
          >
            {mutation.isPending ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Activity Section ─────────────────────────────────────────────────

type ActivityPresentation = {
  title: string
  detail?: React.ReactNode
  icon: LucideIcon
  iconClassName?: string
}

function formatFieldValue(field: string, value: unknown): React.ReactNode {
  if (value == null || value === '') return '—'
  if (field === 'targetDate' || field === 'reviewDate') {
    const d = new Date(value as string)
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString()
  }
  return String(value)
}

function formatEnumValue(value: unknown): string {
  return value == null || value === '' ? '—' : humanizeKey(String(value))
}

function activityPresentation(
  eventType: string,
  payload: Record<string, any>
): ActivityPresentation {
  switch (eventType) {
    case 'treatment_changed':
      return {
        title: 'Treatment updated',
        detail: `Changed from ${formatEnumValue(payload.from)} to ${formatEnumValue(payload.to)}`,
        icon: ShieldCheck,
      }
    case 'status_changed':
      return {
        title: 'Status updated',
        detail: `Changed from ${RISK_STATUS_LABELS[String(payload.from)] ?? formatEnumValue(payload.from)} to ${RISK_STATUS_LABELS[String(payload.to)] ?? formatEnumValue(payload.to)}`,
        icon: CircleDot,
      }
    case 'owner_changed':
      return {
        title: 'Owner updated',
        detail: `Assigned to ${payload.toLabel ?? 'Unassigned'}`,
        icon: UserRound,
      }
    case 'control_linked':
      return {
        title: 'Control linked',
        detail: [payload.controlDisplayCode ?? payload.controlId, payload.controlTitle]
          .filter(Boolean)
          .join(' · '),
        icon: Link2,
      }
    case 'control_unlinked':
      return {
        title: 'Control unlinked',
        detail: [payload.controlDisplayCode ?? payload.controlId, payload.controlTitle]
          .filter(Boolean)
          .join(' · '),
        icon: Unlink,
      }
    case 'test_linked':
      return {
        title: 'Test linked',
        detail: [payload.testCode, payload.testTitle]
          .filter(Boolean)
          .join(' · '),
        icon: Link2,
      }
    case 'test_unlinked':
      return {
        title: 'Test unlinked',
        detail: [payload.testCode, payload.testTitle]
          .filter(Boolean)
          .join(' · '),
        icon: Unlink,
      }
    case 'asset_linked':
      return {
        title: 'Asset linked',
        detail: payload.assetName,
        icon: Box,
      }
    case 'asset_unlinked':
      return {
        title: 'Asset unlinked',
        detail: payload.assetName,
        icon: Box,
      }
    case 'vendor_linked':
      return {
        title: 'Vendor linked',
        detail: payload.vendorName,
        icon: Building2,
      }
    case 'vendor_unlinked':
      return {
        title: 'Vendor unlinked',
        detail: payload.vendorName,
        icon: Building2,
      }
    case 'acceptance_requested':
      return { title: 'Acceptance requested', detail: 'Awaiting an authorized decision', icon: CalendarClock }
    case 'acceptance_rejected':
      return { title: 'Acceptance rejected', detail: payload.reason, icon: XCircle }
    case 'accepted':
      return {
        title: 'Risk accepted',
        detail: payload.expiresAt
          ? `Expires ${formatDate(payload.expiresAt)}`
          : undefined,
        icon: ShieldCheck,
        iconClassName: 'text-status-green',
      }
    case 'acceptance_extended':
      return {
        title: 'Acceptance extended',
        detail: payload.toExpiresAt
          ? `Extended to ${formatDate(payload.toExpiresAt)}`
          : undefined,
        icon: CalendarClock,
      }
    case 'acceptance_revoked':
      return {
        title: 'Acceptance revoked',
        detail: payload.reason,
        icon: XCircle,
        iconClassName: 'text-status-red',
      }
    case 'acceptance_expired':
      return {
        title: 'Acceptance expired',
        detail: payload.expiredAt
          ? `Expired ${formatDate(payload.expiredAt)}`
          : undefined,
        icon: CalendarClock,
        iconClassName: 'text-status-orange',
      }
    case 'field_changed': {
      const field = String(payload.field ?? '')
      const fieldIcon: LucideIcon =
        field === 'targetDate' || field === 'reviewDate'
          ? CalendarDays
          : field === 'likelihood'
            ? TrendingUp
            : field === 'impact'
              ? Target
              : RefreshCw
      return {
        title: `${humanizeKey(field || 'Field')} updated`,
        detail: <>Set to {formatFieldValue(field, payload.to)}</>,
        icon: fieldIcon,
      }
    }
    case 'field_edited':
      return {
        title: `${humanizeKey(String(payload.field ?? 'Field'))} updated`,
        icon: Pencil,
      }
    case 'task_resolved':
      return {
        title: 'Task resolved. Review residual',
        detail: [payload.taskCode, payload.taskTitle]
          .filter(Boolean)
          .join(' · '),
        icon: CheckCircle2,
        iconClassName: 'text-status-green',
      }
    case 'invalidated':
      return { title: 'Risk marked invalid', detail: String(payload.reason ?? ''), icon: AlertCircle }
    case 'restored':
      return { title: 'Risk restored', detail: String(payload.reason ?? ''), icon: RefreshCw }
    case 'assessment_completed':
      return { title: 'Risk assessment completed', icon: CheckCircle2, iconClassName: 'text-status-green' }
    default:
      return { title: humanizeKey(eventType), icon: RefreshCw }
  }
}

export function RiskActivitySection({ riskId }: { riskId: string }) {
  const [limit, setLimit] = useState(20)
  const query = useQuery(
    trpc.riskRegister.listActivity.queryOptions({ riskId, limit })
  )
  const events = query.data?.events ?? []
  const hasMore = !!query.data?.nextCursor

  return (
    <DetailSection title='Activity'>
      <div className={'bg-card rounded-sm'}>
        {query.isLoading ? (
          <div className='px-4 py-4'>
            <p className={EMPTY_TEXT_CLASS}>Loading…</p>
          </div>
        ) : events.length === 0 ? (
          <div className='px-4 py-4 text-center'>
            <p className={EMPTY_TEXT_CLASS}>No activity recorded yet.</p>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className='relative py-2'>
              {events.map((e: any, i: number) => (
                <ActivityRow
                  key={e.id}
                  event={e}
                  showLine={i < events.length - 1}
                />
              ))}
            </div>
          </TooltipProvider>
        )}
        {hasMore && (
          <div className='border-border border-t py-2 text-center'>
            <button
              type='button'
              className='font-body text-cobalt-strong text-[11px] hover:underline'
              onClick={() => setLimit((n) => n + 20)}
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </DetailSection>
  )
}

function ActivityRow({ event, showLine }: { event: any; showLine: boolean }) {
  const presentation = activityPresentation(
    event.eventType,
    event.payload ?? {}
  )
  const EventIcon = presentation.icon
  const isSystem = event.actorKind === 'system'
  const actorName = isSystem
    ? 'Vera'
    : (event.actorName ?? event.actorEmail ?? 'Someone')
  const created = new Date(event.createdAt)

  return (
    <div className='relative flex gap-3 pb-4 pl-[18px] last:pb-2'>
      {showLine && (
        <div className='bg-border absolute top-5 bottom-0 left-[25px] w-px' />
      )}
      <div
        className={cn(
          'text-text-subtle relative z-10 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center',
          presentation.iconClassName
        )}
      >
        <EventIcon className='h-3.5 w-3.5' />
      </div>
      <div className='min-w-0 flex-1 pt-0.5'>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <span className='text-text-secondary text-[12px] leading-tight'>
            {presentation.title}
          </span>
          <span className='text-text-body inline-flex items-center gap-1 text-[11px] leading-tight'>
            <span>by</span>
            <MemberAvatar
              size='xs'
              person={{
                name: event.actorName,
                email: event.actorEmail,
                image: event.actorImage,
                isAgent: isSystem,
              }}
            />
            <span className='text-text-secondary font-medium'>
              {getFirstName(actorName) || actorName}
            </span>
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='text-text-placeholder ml-auto shrink-0 cursor-default text-[11px]'>
                {relativeTime(created, { suffix: true })}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className='text-[12px]'>
                {formatDate(created)}{' '}
                {created.toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        {presentation.detail ? (
          <p className='text-text-subtle mt-0.5 truncate text-[11px] leading-relaxed'>
            {presentation.detail}
          </p>
        ) : null}
      </div>
    </div>
  )
}

// Header pill ────────────────────────────────────────────────────────

export function AcceptedHeaderPill({
  expiresAt,
}: {
  expiresAt: string | Date
}) {
  const d = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt
  return (
    <span className='font-body border-status-blue-border bg-status-blue-subtle text-status-blue-text inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[11px] font-medium'>
      <ShieldCheck className='size-3' />
      Accepted · expires {d.toLocaleDateString()}
    </span>
  )
}

export function ExpiredAcceptanceBanner({
  expiresAt,
}: {
  expiresAt: string | Date
}) {
  const d = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt
  return (
    <div className='border-status-red/30 bg-status-red-bg mb-4 flex items-center gap-2 rounded-sm border px-3 py-2'>
      <AlertTriangle className='text-status-red size-3.5' />
      <p className='font-body text-status-red text-[12px]'>
        Acceptance expired on {d.toLocaleDateString()}. Re-review required.
      </p>
    </div>
  )
}

// Re-export so RiskDetailPanel only imports from one module
export { ExternalLink }
